import { selectUserInfo } from "features/user/userSlice";
import {
  Grid,
  TextField,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ItemGroupProfileService } from "subModule/src/core/services";
import AlertMessages from "common/Alert/AlertMessages";
import { useSnackbar } from "notistack";
import SimpleModal from "common/Form/Form";
import { useNavigate, useParams } from "react-router-dom";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import { ReactComponent as Groups } from "assets/SideBarIcons/Groups.svg";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import { useBusinessSetupEntityBreadcrumbs } from "Helpers/useBusinessSetupEntityBreadcrumbs";
import { useEffect, useState } from "react";
import { GetItemGroupByIdResponse } from "subModule/src/services/itemgroup/interface";
import Loader from "layout/Loader";
import ItemGroupTabs from "./ItemGroupTabs";
import { fetchMediaAsDataUrl } from "Helpers/mediaView";
import MediaImage from "common/MediaImage/MediaImage";
// import CommonCheckbox from "common/CheckBox/Checkbox";
// import { themes } from "theme/themes";
// import { brandingInfo } from "features/server/brandingSlice";
interface FormData {
  [key: string]: any;
}

interface ItemGroupProps {
  setAddNew?: any;
  refetch?: any;
}

export default function ItemGroupModal(props: ItemGroupProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const {
    register,
    control,
    handleSubmit,
    formState: { errors, dirtyFields, isDirty },
    reset,
    watch
  } = useForm<FormData>({ defaultValues: { scrapGroup: false } });
  const loggedInUserData = useSelector(selectUserInfo);
  // const branding = useSelector(brandingInfo);
  const primaryLanguage = loggedInUserData?.business?.primaryLanguage;
  const secondaryLanguage = loggedInUserData?.business?.secondaryLanguage;
  const isArabicLanguage = (lang?: string) => {
    const l = (lang || "").toLowerCase();
    return l === "arabic" || l === "ar" || l.includes("arab");
  };
  const localizedItemGroupPlaceholder = (lang?: string) =>
    isArabicLanguage(lang) ? t("phItemGroupAr") : t("phItemGroupEn");
  // const themeColors = branding?.attributes?.themeId === 100 ? branding?.attributes?.customTheme : (themes as any)[branding?.attributes?.themeId || 3];
  const [loading, setLoading] = useState<boolean>(
    params?.id && !props?.setAddNew ? true : false
  );
  const [IsBtnEnabled, setIsBtnEnabled] = useState<boolean>(
    params?.id && !props?.setAddNew ? false : true
  );
  const { breadcrumbItems, captureLabel } = useBusinessSetupEntityBreadcrumbs({
    sectionLabelKey: "Items",
    moduleLabelKey: "itemsGroup",
    moduleHref: "/parts-group",
    entityFallbackLabelKey: "title",
    isEdit: !!params?.id && !props?.setAddNew,
  });
  const [fileName, setFileName] = useState<string>('');
  const [imagePreview, setImagePreview] = useState<string>('');
  useEffect(() => {
    if (params?.id && !props?.setAddNew) {
      if (loading) getByID();
    }
  }, [params]);

  useEffect(() => {
    if (isDirty && params?.id) setIsBtnEnabled(true);
  }, [isDirty, dirtyFields]);

  const getByID = () => {
    ItemGroupProfileService.getItemGroupById(+(params as any)?.id)
      .then(async (res: GetItemGroupByIdResponse) => {
        if (res.success) {
          reset({ ...(res?.data || {}) });
          captureLabel(res?.data?.title);
          const preview = await fetchMediaAsDataUrl(res?.data?.imageUrl || "");
          setImagePreview(preview);
          setLoading(false);
        } else {
          enqueueSnackbar(
            <AlertMessages text={res.message || "Something went wrong"} />,
            { variant: "error" }
          );
        }
      })
      .catch((err: any) => {
        console.error("===", err);
      });
  };

  const onSubmit = (data: any) => {
    setIsBtnEnabled(false);
    if (!data.file) delete data.file;
    if (data.file && !data.file.type.startsWith('image/')) {
      setIsBtnEnabled(true);
      enqueueSnackbar(<AlertMessages text={t('onlyImagesAllowed')} />, { variant: 'error' });
      return;
    }
    if (data.file && data.file.size > 10 * 1024 * 1024) {
      setIsBtnEnabled(true);
      enqueueSnackbar(<AlertMessages text={t('fileSmaller10MB')} />, { variant: 'error' });
      return;
    }
    if (params?.id && !props?.setAddNew) {
      const imgUrl = data?.imageUrl || '';
      let imgFileName = '';
      if (imgUrl) {
        try {
          imgFileName = new URL(imgUrl).pathname.split('/').pop() || '';
        } catch {
          imgFileName = imgUrl.split('?')[0].split('/').pop() || '';
        }
        delete data.imageUrl;
      }
      ItemGroupProfileService.updateItemGroup({
        ...data, 
        oldUrl: imgFileName || ''
      })
        .then((response: any) => {
          if (response.success) {
            captureLabel(data?.title);
            getByID();
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={
                  t("itemGroup") + " " + t("updated") + " " + t("successfully")
                }
              />,
              { variant: "success" }
            );
          } else {
            enqueueSnackbar(<AlertMessages text={response?.message} />, {
              variant: "error",
            });
            setIsBtnEnabled(true);
          }
        })
        .catch((err: any) => {
          console.error("===", err);
          setIsBtnEnabled(true);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" }
          );
        });
    } else {
      ItemGroupProfileService.createItemGroup(data).then((response: any) => {
        if (response.success) {
          enqueueSnackbar(
            <AlertMessages
              statusCode={response.status}
              text={
                t("itemGroup") + " " + t("created") + " " + t("successfully")
              }
            />,
            { variant: "success" }
          );
          if (props?.setAddNew) {
            props?.setAddNew("");
            props.refetch();
          } else navigate("/parts-group");
        } else {
          setIsBtnEnabled(true);
          enqueueSnackbar(<AlertMessages text={response?.message} />, {
            variant: "error",
          });
        }
      })
        .catch((err: any) => {
          console.error("===", err);
          setIsBtnEnabled(true);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" }
          );
        });
    }
  };

  return (
    <>
      {props?.setAddNew ? null : (
        <ComponentHeaderTwo
          showBtn={true}
          showSaveBtn={true}
          onClick={() => navigate("/parts-group")}
          onSubmit={handleSubmit(onSubmit)}
          heading={
            params?.id && !props?.setAddNew
              ? t("itemsGroup")
              : t("addItemGroup")
          }
          icon={<Groups height={28} width={28} />}
          btnText={"back"}
          btnType={"back"}
          isBtnEnabled={IsBtnEnabled}
          showCreateBtn={checkPrivileges("ROLE_ITEMGROUP_CREATE")}
          breadcrumbItems={breadcrumbItems}
        />
      )}
      <div className={`${params?.id && !props?.setAddNew ? "flex" : ""}`}>
        {params?.id && !props?.setAddNew ? (
          <ItemGroupTabs params={params} selectedIndex={0} />
        ) : null}
        {loading ? (
          <Loader />
        ) : (
          <SimpleModal
            visable={true}
            title={
              params?.id && !props?.setAddNew
                ? t("itemsGroup")
                : t("addItemGroup")
            }
            isButtonVisable={true}
            isVisableBtn={IsBtnEnabled}
            btnclosetext="Back"
            buttonText={params?.id && !props?.setAddNew ? "update" : "submit"}
            attBtnNotshow={false}
            notVisableBackbtn={true}
            showPortList={handleSubmit(onSubmit)}
            formSubmit={handleSubmit(onSubmit)}
            entityPopup={props?.setAddNew}
            padding={!props?.setAddNew}
            content={
              <Grid
                container
                component="form"
                id="form"
                spacing={2}
                onSubmit={handleSubmit(onSubmit)}
              >
                <Grid item xs={12} md={6} lg={3}>
                  <TextField
                    {...register("title", {
                      validate: {
                        required: (value) =>
                          value.trim() !== "" || t("This field is required."),
                      },
                    })}
                    label={`${!primaryLanguage ? t("primary") : ""} ${t("title")} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ""} *`}
                    placeholder={localizedItemGroupPlaceholder(primaryLanguage)}
                    title={(`${!primaryLanguage ? t("primary") : ""} ${t("title")} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ""} *`).replace(/\s+/g, ' ').trim()}
                    fullWidth
                    error={!!errors.title}
                    helperText={errors.title?.message as string}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6} lg={3}>
                  <TextField
                    {...register("secondaryTitle")}
                    label={`${!secondaryLanguage ? t("secondary") : ""} ${t("title")} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""}`}
                    placeholder={localizedItemGroupPlaceholder(secondaryLanguage)}
                    title={(`${!secondaryLanguage ? t("secondary") : ""} ${t("title")} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""}`).replace(/\s+/g, ' ').trim()}
                    fullWidth
                    error={!!errors.secondaryTitle}
                    helperText={errors.secondaryTitle?.message as string}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                {/* <Grid item xs={12} md={6} lg={3}>
                  <Controller
                    name="scrapGroup"
                    control={control}
                    defaultValue={false}
                    render={({ field }) => (
                      <CommonCheckbox
                        {...field}
                        label={t("Scrap Group")}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => field.onChange(event.target.checked)}
                        checked={field.value}
                        color={themeColors?.primary}
                      />
                    )}
                  />
                </Grid> */}
                <Grid item xs={12} md={3} id="importfile">
                  <Controller
                    name="file"
                    control={control}
                    rules={{ required: false }}
                    render={({ field }) => (<>
                      <p className='font-Saira text-[13px] leading-[20px] text-[#343434] font-semibold text-start'>{t('icon')}</p>
                      <input
                        type="file"
                        required
                        accept="image/*"
                        style={{ color: fileName ? 'inherit' : 'transparent' }}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                          const file = event.target.files?.[0];
                          setFileName(file ? file.name : '');
                          field.onChange(file);
                        }}
                        title={fileName}
                      />
                    </>)}
                  />
                  <MediaImage src={imagePreview} />
                </Grid>
              </Grid>
            }
          />
        )}
      </div>
    </>
  );
}
